import { RFIProps } from "../../../interfaces/rfi_types"
import { useAuth } from "../../../App"
import { RFIFormBase } from "./RFIFormBase"
import { useForm } from "react-hook-form"
import { z } from "zod"
import { zodResolver } from "@hookform/resolvers/zod"
import { ProjectSchema } from "@/components/_Shared/Form/Schemas/ProjectSchema"
import { CategorySchema } from "@/components/_Shared/Form/Schemas/Category"
import { AssignedToSchema, CreatedBySchema } from "@/components/_Shared/Form/Schemas/ManagerSchema"
import { Error_Component } from "@/components/misc"
import { useFormData } from "@/components/_Shared/Form/Hooks/useFormDataFetch"
import { useEffect, useState } from "react"
import { StatusSchema } from "@/components/_Shared/Form/Schemas/StatusSchema"
import { CreateOrUpdate } from "@/components/_Shared/Form/Utils/MethodHandler"
import { updateRFI } from "@/api/rfi"
import { createRFI } from "@/api/rfi"
import { EmployeeProps } from "@/interfaces/employee_type"
import { Alert } from "@/components/_Shared/Form/Buttons/Alert"

const rfiFormSchema = z.object({
    RFI_id: z.string().optional(),  // Only used for PUT requests
    RFI_number: z.coerce.number({
        invalid_type_error: "RFI Number must be a number"
    }).optional(),
    sent_out_date: z.string().optional(),
    date_received: z.string().min(1),
    description: z.string().min(1, "Description is required"),
    alert_status: z.boolean().optional(),
})
.merge(ProjectSchema)
.merge(CategorySchema)
.merge(AssignedToSchema)
.merge(CreatedBySchema)
.merge(StatusSchema)

export type RFIFormData = z.infer<typeof rfiFormSchema>

export function RFIForm({RFIProps, method}: {RFIProps?: RFIProps, method: "POST" | "PUT"}) {
    const [folder_created, setFolderCreated] = useState(false)
    const { user } = useAuth()
    const { fetchAllData, projects, projectManagers, categories } = useFormData()

    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting, isSubmitSuccessful },
        setError,
        control
    } = useForm<RFIFormData>({
        resolver: zodResolver(rfiFormSchema),
        defaultValues: RFIProps ? ImportRFIProps(RFIProps) : DefaultRFIProps(user as EmployeeProps)
    })

    async function onSubmit(data: RFIFormData) {
        const data_to_send: RFIProps = {
            ...data,
            project: data.project?.value ?? "",
            project_id: data.project?.value ?? "",
            RFI_number: data.RFI_number?.toString() ?? "",
            created_by_pk: data.created_by?.value ?? "",
            assigned_to_pk: data.assigned_to?.value ?? "",
            sent_out_date: data.sent_out_date ?? "",
            category: data.category?.value as "Mechanical" | "Electrical" | "Plumbing" | "Fire Protection" | "Other",
            status: data.status?.value as "Active" | "Closed",
            created_by: undefined,
            assigned_to: undefined,
        }

        const method_handler = CreateOrUpdate(method, createRFI, updateRFI)

        const response = await method_handler(data_to_send)

        if (response.status === 400) {
            setError("root", {message: "An error occurred while submitting the RFI form."})
        } else if (response.status === 403) {
            setError("root", {message: "You are not authorized to submit this RFI."})
        } else if (response.status === 500) {
            setError("root", {message: "An error occurred while submitting the RFI form."})
        }

        // Used to check if the folder was created successfully (when creating a RFI)
        // If not, we need to alert the user that the folder was not created successfully
        if (response.data && 'folder_created' in response.data) {
            setFolderCreated(response.data.folder_created)
        }
    }

    useEffect(() => {
        fetchAllData()
    }, [fetchAllData])

    return (
        <> 
            {errors.root && <Error_Component errorString={errors.root?.message ?? "An error occurred"}/>}

            <RFIFormBase
                rfi={RFIProps ?? null}
                errors={errors} 
                control={control} 
                register={register} 
                isProcessing={isSubmitting}
                method={method}
                onSubmit={handleSubmit(onSubmit)}
                project_list={projects}
                employee_list={projectManagers}
                category_list={categories}
            />

            {isSubmitSuccessful && <Alert
                title={method === "POST" ? "RFI Created" : "RFI Updated"}
                description={setDescription(method, folder_created)}
                redirects={[
                    {
                        label: "View RFIs",
                        value: "/rfis",
                    },
                    {
                        label: "To Dashboard",
                        value: "/main_menu",
                    },
                    {
                        label: "Create Another RFI",
                        value: "/rfis/create_rfi",
                    }
                ]}
            />}
        </>
    )
}

function ImportRFIProps(RFIProps: RFIProps): RFIFormData {
    return {
        RFI_id: RFIProps.RFI_id,
        RFI_number: parseInt(RFIProps.RFI_number ?? "0"),
        sent_out_date: RFIProps.sent_out_date,
        date_received: RFIProps.date_received,
        description: RFIProps.description,
        status: {value: RFIProps.status, label: RFIProps.status},
        project: {value: RFIProps.project_id ?? "", label: RFIProps.project_name ?? ""},
        category: {value: RFIProps.category, label: RFIProps.category},
        assigned_to: {value: RFIProps.assigned_to?.email ?? "", label: RFIProps.assigned_to?.name ?? ""},
        created_by: {value: RFIProps.created_by?.email ?? "", label: RFIProps.created_by?.name ?? ""},
        alert_status: RFIProps.alert_status,
    }
}

function DefaultRFIProps(user: EmployeeProps): RFIFormData  {
    return {
        RFI_id: "",
        RFI_number: 0,
        sent_out_date: '',
        date_received: new Date().toLocaleDateString('en-CA'),
        description: "",
        status: {value: "Active", label: "Active"},
        project: null,
        category: {value: "Select", label: "Select"},
        assigned_to: null,
        created_by: {value: user.email ?? "", label: user.name ?? ""},
        alert_status: false,
    }
}

// Just an easy way to set the description of the alert based on the method and if the folder was created successfully
function setDescription(method: "POST" | "PUT", folder_created: boolean) {
    if (method === "POST" && folder_created) {
        return "The RFI has been created successfully."
    } else if (method === "POST" && !folder_created) {
        return "The RFI has been created successfully, however, there was an error creating the folder. Please manually create the folder."
    } else if (method === "PUT") {
        return "The RFI has been updated successfully."
    } else {
        return "An error occurred while submitting the RFI form."
    }
}